import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import clsx from "clsx";
import API from "../services/http-helper.service";
import ImagePopup from "../review-portal/components/form-element/ImagePopup";
import { ImageComponent } from "./ImageComponent";

const AI_STATUS = {
  VERIFIED: "verified",
  FAILED: "failed",
  ACKNOWLEDGED: "acknowledged",
};

const AI_VERIFICATION_STATUS_MAP = {
  SUCCESS: AI_STATUS.VERIFIED,
  FAILED: AI_STATUS.FAILED,
  ACKNOWLEDGED: AI_STATUS.ACKNOWLEDGED,
};

const getAiStatus = (aiVerificationStatus) => {
  if (!aiVerificationStatus) return null;
  return AI_VERIFICATION_STATUS_MAP[aiVerificationStatus] || null;
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    marginTop: 0,
  },
  loaderBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "75px",
    width: "100px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    border: "1px dashed #bdbdbd",
  },
  loaderText: {
    marginTop: "6px",
    fontSize: "10px",
    color: "#9e9e9e",
  },
  imageContainer: {
    position: "relative",
    display: "inline-block",
    overflow: "hidden",
    borderRadius: 4,
    flexShrink: 0,
  },
  borderVerified: {
    border: "3px solid #4caf50",
  },
  borderFailed: {
    border: "3px solid #ff9800",
  },
  borderAcknowledged: {
    border: "3px solid #2196f3",
  },
  diagonalStamp: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(-35deg)",
    padding: "2px 40px",
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#fff",
    whiteSpace: "nowrap",
    width: "160%",
    textAlign: "center",
    pointerEvents: "none",
    letterSpacing: 1,
  },
  stampVerified: {
    backgroundColor: "rgba(76, 175, 80, 0.70)",
  },
  stampAcknowledged: {
    backgroundColor: "rgba(33, 150, 243, 0.70)",
  },
  stampFailed: {
    backgroundColor: "rgba(255, 152, 0, 0.70)",
    fontSize: 8,
    letterSpacing: 0.5,
    lineHeight: 1.3,
    padding: "2px 24px",
  },
  statusChip: {
    height: 24,
    fontSize: 11,
    fontWeight: 600,
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  aiMessage: {
    display: "block",
    fontSize: 11,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    marginTop: -theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  statusVerified: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    borderColor: "#4caf50",
  },
  statusFailed: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    borderColor: "#ff9800",
  },
  statusAcknowledged: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    borderColor: "#2196f3",
  },
}));

export function AiStatusOverlay({
  imageList,
  aiVerificationStatus,
  aiVerificationMessage,
}) {
  const classes = useStyles();
  const status = getAiStatus(aiVerificationStatus);

  const [imageDataList, setImageDataList] = useState([]);
  const [loadingIds, setLoadingIds] = useState([]);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageDetails, setImageDetails] = useState({});

  useEffect(() => {
    if (status && imageList?.length > 0) {
      setImageDataList([]);
      const ids = imageList
        .map((val) => (typeof val === "string" ? val : val.imageId))
        .filter(Boolean);
      setLoadingIds(ids);
      ids.forEach((id) => getFormImage(id));
    }
  }, [imageList, status]);

  const getFormImage = async (id) => {
    try {
      let res = await API.get(`images/${id}`);
      if (res.status === 200) {
        setImageDataList((prevData) => [
          ...prevData,
          {
            imageId: id,
            fileUrl: res?.data?.attachments?.[0]?.file || "",
            fileName: res?.data?.attachments?.[0]?.fileName || "",
          },
        ]);
      }
    } catch (e) {
      return null;
    } finally {
      setLoadingIds((prev) => prev.filter((imgId) => imgId !== id));
    }
  };

  const onClickAvatar = (item) => {
    setImageDetails(item);
    setImageOpen(true);
  };

  const onClose = () => {
    setImageOpen(false);
  };

  if (!status) {
    return <ImageComponent imageList={imageList} />;
  }

  if (!imageList?.length) return null;

  const statusChipElement = (
    <Box className={classes.chipRow}>
      {status === AI_STATUS.VERIFIED && (
        <Chip
          icon={<CheckCircleIcon style={{ color: "#2e7d32" }} />}
          label="AI Verified"
          variant="outlined"
          size="small"
          className={clsx(classes.statusChip, classes.statusVerified)}
        />
      )}
      {status === AI_STATUS.FAILED && (
        <Chip
          icon={<WarningIcon style={{ color: "#e65100" }} />}
          label="AI Verification Failed"
          variant="outlined"
          size="small"
          className={clsx(classes.statusChip, classes.statusFailed)}
        />
      )}
      {status === AI_STATUS.ACKNOWLEDGED && (
        <Chip
          icon={<InfoIcon style={{ color: "#1565c0" }} />}
          label="Acknowledged"
          variant="outlined"
          size="small"
          className={clsx(classes.statusChip, classes.statusAcknowledged)}
        />
      )}
    </Box>
  );

  const stampLabel =
    status === AI_STATUS.VERIFIED
      ? "AI Verified"
      : status === AI_STATUS.ACKNOWLEDGED
      ? "Acknowledged"
      : "AI Verification Failed";

  const stampClass =
    status === AI_STATUS.VERIFIED
      ? classes.stampVerified
      : status === AI_STATUS.ACKNOWLEDGED
      ? classes.stampAcknowledged
      : classes.stampFailed;

  return (
    <Card className={classes.root}>
      <Box>
        {statusChipElement}
        {aiVerificationMessage?.trim() ? (
          <Typography
            variant="caption"
            className={classes.aiMessage}
            data-testid="ai-verification-message"
          >
            <b>AI Verification Message:</b> {aiVerificationMessage}
          </Typography>
        ) : null}
        <Grid container spacing={2} alignItems="center">
          {imageList.map((val, index) => {
            const id = typeof val === "string" ? val : val.imageId;
            const isLoading = loadingIds.includes(id);
            const loaded = imageDataList.find((img) => img.imageId === id);
            return (
              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                {isLoading ? (
                  <Box className={classes.loaderBox}>
                    <CircularProgress size={24} thickness={4} />
                    <Typography className={classes.loaderText}>
                      Loading...
                    </Typography>
                  </Box>
                ) : loaded ? (
                  <Box
                    className={clsx(classes.imageContainer, {
                      [classes.borderVerified]:
                        status === AI_STATUS.VERIFIED,
                      [classes.borderFailed]: status === AI_STATUS.FAILED,
                      [classes.borderAcknowledged]:
                        status === AI_STATUS.ACKNOWLEDGED,
                    })}
                  >
                    <img
                      src={loaded.fileUrl}
                      alt={`image-${index + 1}`}
                      style={{
                        height: "75px",
                        width: "100px",
                        display: "block",
                        cursor: "pointer",
                      }}
                      onClick={() => onClickAvatar(loaded)}
                    />
                    <Box
                      className={clsx(classes.diagonalStamp, stampClass)}
                    >
                      {stampLabel}
                    </Box>
                  </Box>
                ) : null}
              </Grid>
            );
          })}
          <ImagePopup
            open={imageOpen}
            onClose={onClose}
            imageUrl={imageDetails.fileUrl}
            title={imageDetails.fileName}
          />
        </Grid>
      </Box>
    </Card>
  );
}
